import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

const Kubernetes = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Kubernetes & Containerized Workloads - limkc</title>
        <meta name="description" content="Learn about Kubernetes orchestration, AWS EKS, and containerized workloads with real-world examples." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
  
      <div className="w-full h-full overflow-auto">
        {/* Banner Image - Full Width */}
        <div className="w-full h-48 overflow-hidden" style={{ marginTop: 'var(--navbar-height)' }}>
          <img
            src="/IMAGE/kubernetes.png"
            alt="Kubernetes and Containerized Workloads"
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Content - Centered */}
        <div className="w-full flex justify-center">
          <div className="py-6 transition-all duration-300 w-full"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--text-primary)',
              maxWidth: '1200px',
              paddingLeft: '1rem',
              paddingRight: '1rem'
            }}>

            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Kubernetes & Containerized Workloads</h1>
          <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>
            Kubernetes is an open-source container orchestration system that automates deployment, 
            scaling, and management of containerized applications. AWS EKS provides a managed 
            Kubernetes service that simplifies running Kubernetes clusters on AWS.
          </p>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Why Kubernetes?</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Kubernetes is essential for modern cloud-native applications because:
          </p>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li>Ensures high availability by automatically replacing failed containers.</li>
            <li>Provides scalability through auto-scaling policies.</li>
            <li>Facilitates microservices with service discovery and load balancing.</li>
            <li>Supports multi-cloud & hybrid deployments.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Key Components of Kubernetes</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><b>Pods & Deployments</b> – Fundamental units in Kubernetes.</li>
            <li><b>Services</b> – Enable communication between microservices.</li>
            <li><b>Ingress</b> – Manages external access to services.</li>
            <li><b>ConfigMaps & Secrets</b> – Handle configuration and sensitive data.</li>
            <li><b>Horizontal Pod Autoscaler (HPA)</b> – Adjusts replicas based on demand.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Real World Experience</h2>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            I deployed Kubernetes on AWS EKS for a high-traffic SaaS application.  
            The biggest challenges included managing persistent storage, handling zero-downtime deployments,  
            and optimizing autoscaling policies for cost efficiency.
          </p>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Common Challenges & Solutions</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><b>Cold Starts</b> – Use warm pools to reduce startup time.</li>
            <li><b>Networking Issues</b> – Implement Calico for better network policy control.</li>
            <li><b>Monitoring & Logging</b> – Use Prometheus & Grafana for better observability.</li>
          </ul>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Basic Kubernetes Deployment</h2>
          <pre className="p-4 rounded-md mt-2 text-sm" style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-on-dark)' }}>
  {`
  apiVersion: apps/v1
  kind: Deployment
  metadata:
    name: my-app
  spec:
    replicas: 3
    selector:
      matchLabels:
        app: my-app
    template:
      metadata:
        labels:
          app: my-app
      spec:
        containers:
        - name: my-app
          image: my-app:latest
          ports:
          - containerPort: 80
  `}
          </pre>
  
          <h2 className="text-2xl font-semibold mt-6" style={{ color: 'var(--text-primary)' }}>Related Topics</h2>
          <ul className="list-disc list-inside mt-2" style={{ color: 'var(--text-secondary)' }}>
            <li><a href="/topics/cloud-management" className="text-blue-500 hover:underline">Cloud Management & Multi-Cloud Strategy</a></li>
            <li><a href="/topics/serverless" className="text-blue-500 hover:underline">Serverless & API Gateway</a></li>
          </ul>
  
          <button 
            onClick={() => navigate("/projects")} 
            className="mt-6 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--text-secondary)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
          >
            ← Back to Topics
          </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Kubernetes;
